import { logExceptionIntoSentry } from '../error-logging';
import { appcuesProvider } from './providers/appcues';
import { ga4Provider } from './providers/ga4';
import { intercomProvider } from './providers/intercom';
import type {
  AnalyticsEvent,
  AnalyticsGroup,
  AnalyticsIdentity,
  AnalyticsPageView,
  AnalyticsProvider,
} from './providers/types';

const providers: readonly AnalyticsProvider[] = [appcuesProvider, ga4Provider, intercomProvider];

const providersReportedAsFailing = new Set<string>();

/** A provider that throws on every event must not flood Sentry. */
const reportProviderFailure = (provider: AnalyticsProvider, error: Error) => {
  if (!providersReportedAsFailing.has(provider.name)) {
    providersReportedAsFailing.add(provider.name);
    logExceptionIntoSentry(error, { analyticsProvider: provider.name });
  }
};

/** One failing provider must not stop the others. */
const forEachProvider = (action: (provider: AnalyticsProvider) => void) => {
  providers.forEach((provider) => {
    try {
      action(provider);
    } catch (error) {
      reportProviderFailure(provider, error as Error);
    }
  });
};

/**
 * A user is identified once, when their profile loads. Accepting cookies after that point loads the providers
 * with no one identified, so the identity is replayed rather than dropped the way pre-consent events are.
 */
let lastIdentity: AnalyticsIdentity | undefined;
let lastGroup: AnalyticsGroup | undefined;

export const identifyAnalyticsUser = (identity: AnalyticsIdentity) => {
  lastIdentity = identity;
  forEachProvider((provider) => provider.identify(identity));
};

export const groupAnalyticsUser = (group: AnalyticsGroup) => {
  lastGroup = group;
  forEachProvider((provider) => provider.group(group));
};

export const loadAnalyticsProviders = () => {
  forEachProvider((provider) => provider.load());

  if (lastIdentity) {
    identifyAnalyticsUser(lastIdentity);
  }
  if (lastGroup) {
    groupAnalyticsUser(lastGroup);
  }
};

export const trackAnalyticsPageView = (pageView: AnalyticsPageView) =>
  forEachProvider((provider) => provider.page(pageView));

export const trackAnalyticsEvent = (event: AnalyticsEvent) => forEachProvider((provider) => provider.track(event));

/**
 * Resolves once every provider has torn its session down. Logout awaits this before navigating, otherwise the
 * next user of the browser can inherit the previous user's Intercom session.
 */
export const resetAnalyticsUser = async (): Promise<void> => {
  lastIdentity = undefined;
  lastGroup = undefined;

  await Promise.all(
    providers.map(async (provider) => {
      try {
        await provider.reset();
      } catch (error) {
        reportProviderFailure(provider, error as Error);
      }
    }),
  );
};
